/* ------------------------------------*\
    #TABS SLIDER SCRIPT
\*------------------------------------ */

import Component from '../../../../src/js/module/component';

export default class Tabs extends Component {

    static componentName = 'tabs';

    static componentSelector = '.js-tabs';

    static defaultOptions = {

    };

    constructor() {
        super(...arguments);

        this.tablist = this.element.querySelector('[role="tablist"]');
        this.tabs = Array.from(this.element.querySelectorAll('[role="tab"]'));
        this.tabpanels = Array.from(this.element.querySelectorAll('[role="tabpanel"]'));
        this.currentTab = this.element.querySelector('[aria-selected="true"]') || (this.tabs[0].setAttribute('aria-selected', 'true'), this.tabs[0]);
        this.currentTabPanel = this.element.querySelector(`#${this.currentTab?.getAttribute('aria-controls')}`);
        this.currentIndex = this.getTabIndex(this.currentTab);
        this.initTabs();
    }

    initTabs() {
        this.scrollTabIntoView();

        this.tabs.forEach((tab) => {
            const selected = (tab.getAttribute('aria-selected') === 'true');

            if (!selected) {
                // Non-selected tabs are excluded from focus-order to allow focus on selected panel.
                tab.setAttribute('tabindex', -1);
            }

            tab.addEventListener('click', this.onTabClick.bind(this));
            tab.addEventListener('keydown', this.onKeyDown.bind(this));
            tab.addEventListener('focus', (event) => event.currentTarget.scrollIntoView({
                // avoid vertical scrolling
                block: 'nearest',
                // analogue to `scroll-snap-align: center`
                inline: 'center',
            }));
        });

        this.tabpanels.forEach((tabpanel) => {
            if (tabpanel === this.currentTabPanel) {
                this.switchTab(this.currentTab);
            }

            // make non-focusable container programmatically focusable
            tabpanel.setAttribute('tabindex', -1);
        });
    }

    /**
     * Ensure horizontal scroll position is correct for current tab.
     */
    scrollTabIntoView() {
        this.tablist.scrollTo({
            left: (this.currentTab.offsetLeft - this.tablist.offsetLeft),
        });
    }

    onTabClick(event) {
        this.switchTab(event.currentTarget);
    }

    getTabIndex(tab) {
        return this.tabs.indexOf(tab);
    }

    getNextTab(tab) {
        const index = this.getTabIndex(tab);
        const next = (index + 1) % this.tabs.length; // Wrap around (go to first when current is last)

        return this.tabs[next];
    }

    getPreviousTab(tab) {
        const index = this.getTabIndex(tab);
        const previous = (index - 1 + this.tabs.length) % this.tabs.length; // Wrap around (go to last when current is first)

        return this.tabs[previous];
    }

    onKeyDown(event) {
        const { key } = event;
        const tab = event.currentTarget;
        const nextTab = this.getNextTab(tab);
        const previousTab = this.getPreviousTab(tab);

        switch (key) {
            // focus next
            case 'ArrowRight':
                event.preventDefault();
                nextTab?.focus();
                break;

            // focus previous
            case 'ArrowLeft':
                event.preventDefault();
                previousTab?.focus();
                break;

            // focus first
            case 'Home':
                event.preventDefault();
                this.tabs.at(0)?.focus();
                break;

            // focus last
            case 'End':
                event.preventDefault();
                this.tabs.at(-1)?.focus();
                break;

            // focus tabpanel
            case 'ArrowDown':
                event.preventDefault();
                this.currentTabPanel?.focus();
                break;

            default:
                break;
        }
    }

    switchTab(tab) {
        if (tab.hasAttribute('aria-disabled')) {
            return;
        }

        const id = tab.getAttribute('aria-controls');
        const tabpanel = this.element.querySelector(`#${id}`);

        // old tab
        this.currentTab.setAttribute('aria-selected', 'false');
        this.currentTab.setAttribute('tabindex', -1);
        this.currentTabPanel.setAttribute('aria-hidden', 'true');

        // new tab
        tab.setAttribute('aria-selected', 'true');
        tab.removeAttribute('tabindex');
        tabpanel.setAttribute('aria-hidden', 'false');
        this.currentTab = tab;
        this.currentTabPanel = tabpanel;
        this.currentIndex = this.getTabIndex(tab);
    }

    /**
     * Scroll by item
     * @param {number} [direction=1] - The number designating the scroll direction, where 1 is left and -1 is right.
     */
    scrollList(direction = 1) {
        requestAnimationFrame(() => {
            this.tablist.scrollBy({
                left: (-(this.tablist.offsetWidth / this.tabs.length) * direction),
                behavior: 'smooth',
            });
        });
    }

}

Tabs.register();
